import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { User, MonthlySchedule, HistoricalSchedule, DayOfWeek } from '../types';
import { mockApiClient, UserWish } from './mock-api';

// API Response types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: any;
    timestamp: Date;
  };
}

export interface ApiError {
  code: string;
  message: string;
  details?: any;
  timestamp: Date;
}

// API Client configuration
// In Create React App, environment variables are injected at build time
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || (
  process.env.NODE_ENV === 'production' 
    ? 'https://api.care-schedule.com' 
    : 'http://localhost:3001'
);

// Check if we should use mock API for development
const USE_MOCK_API = process.env.REACT_APP_USE_MOCK_API === 'true' || process.env.NODE_ENV === 'development';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000, // 30 seconds
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Request interceptor to add auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor for unified error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        // Check if the response indicates an API error
        if (response.data && !response.data.success) {
          throw new ApiClientError(
            response.data.error?.code || 'API_ERROR',
            response.data.error?.message || 'An error occurred',
            response.data.error?.details
          );
        }
        return response;
      },
      (error) => {
        if (error.response) {
          // Server responded with error status
          const status = error.response.status;
          const data = error.response.data;

          if (status === 401) {
            // Unauthorized - clear token and redirect to login
            localStorage.removeItem('authToken');
            window.location.href = '/login';
            throw new ApiClientError('UNAUTHORIZED', '認証が必要です。再度ログインしてください。');
          } else if (status === 403) {
            throw new ApiClientError('FORBIDDEN', 'この操作を実行する権限がありません。');
          } else if (status === 404) {
            throw new ApiClientError('NOT_FOUND', 'リソースが見つかりません。');
          } else if (status >= 500) {
            throw new ApiClientError('SERVER_ERROR', 'サーバーエラーが発生しました。しばらく時間をおいて再試行してください。');
          } else if (data && data.error) {
            throw new ApiClientError(
              data.error.code || 'API_ERROR',
              data.error.message || 'An error occurred',
              data.error.details
            );
          }
        } else if (error.request) {
          // Network error
          throw new ApiClientError('NETWORK_ERROR', 'ネットワークエラーが発生しました。インターネット接続を確認してください。');
        }

        // Other errors
        throw new ApiClientError('UNKNOWN_ERROR', error.message || 'Unknown error occurred');
      }
    );
  }

  // Generic request method
  private async request<T>(config: AxiosRequestConfig): Promise<T> {
    const response = await this.client.request<ApiResponse<T>>(config);
    return response.data.data as T;
  }

  // User Management API
  async getUsers(): Promise<User[]> {
    if (USE_MOCK_API) {
      const response = await mockApiClient.getAllUsers();
      if (!response.success) {
        throw new ApiClientError(
          response.error?.code || 'API_ERROR',
          response.error?.message || 'Failed to get users'
        );
      }
      return response.data!;
    }

    return this.request<User[]>({
      method: 'GET',
      url: '/users',
    });
  }

  async getUser(id: string): Promise<User> {
    if (USE_MOCK_API) {
      const response = await mockApiClient.getUser(id);
      if (!response.success) {
        throw new ApiClientError(
          response.error?.code || 'API_ERROR',
          response.error?.message || 'Failed to get user'
        );
      }
      return response.data!;
    }

    return this.request<User>({
      method: 'GET',
      url: `/users/${id}`,
    });
  }

  async createUser(name: string, availableDays: DayOfWeek[]): Promise<User> {
    if (USE_MOCK_API) {
      const response = await mockApiClient.createUser({ name, availableDays });
      if (!response.success) {
        throw new ApiClientError(
          response.error?.code || 'API_ERROR',
          response.error?.message || 'Failed to create user'
        );
      }
      return response.data!;
    }

    return this.request<User>({
      method: 'POST',
      url: '/users',
      data: { name, availableDays },
    });
  }

  async updateUser(id: string, data: { name?: string; availableDays?: DayOfWeek[] }): Promise<User> {
    return this.request<User>({
      method: 'PUT',
      url: `/users/${id}`,
      data,
    });
  }

  async deleteUser(id: string): Promise<void> {
    return this.request<void>({
      method: 'DELETE',
      url: `/users/${id}`,
    });
  }

  // Excel Upload API
  async uploadExcelFile(file: File): Promise<HistoricalSchedule> {
    if (USE_MOCK_API) {
      const response = await mockApiClient.uploadExcelFile(file);
      if (!response.success) {
        throw new ApiClientError(
          response.error?.code || 'API_ERROR',
          response.error?.message || 'Failed to upload file'
        );
      }
      return response.data!;
    }

    const formData = new FormData();
    formData.append('file', file);

    return this.request<HistoricalSchedule>({
      method: 'POST',
      url: '/upload-schedule',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60 seconds for file upload
    });
  }

  async getHistoricalSchedule(year: number, month: number): Promise<HistoricalSchedule> {
    return this.request<HistoricalSchedule>({
      method: 'GET',
      url: '/historical-schedule',
      params: { year, month },
    });
  }

  // Schedule Generation API
  async generateSchedule(
    year: number,
    month: number,
    historicalYear: number,
    historicalMonth: number
  ): Promise<MonthlySchedule> {
    if (USE_MOCK_API) {
      const response = await mockApiClient.generateSchedule(year, month);
      if (!response.success) {
        throw new ApiClientError(
          response.error?.code || 'API_ERROR',
          response.error?.message || 'Failed to generate schedule'
        );
      }
      return response.data!;
    }

    return this.request<MonthlySchedule>({
      method: 'POST',
      url: '/generate-schedule',
      data: { year, month, historicalYear, historicalMonth },
      timeout: 60000, // 60 seconds for schedule generation
    });
  }

  // Schedule Management API
  async getSchedule(year: number, month: number): Promise<MonthlySchedule | null> {
    if (USE_MOCK_API) {
      const response = await mockApiClient.getSchedule(year, month);
      if (!response.success) {
        if (response.error?.code === 'SCHEDULE_NOT_FOUND') {
          return null;
        }
        throw new ApiClientError(
          response.error?.code || 'API_ERROR',
          response.error?.message || 'Failed to get schedule'
        );
      }
      return response.data!;
    }

    try {
      return await this.request<MonthlySchedule>({
        method: 'GET',
        url: `/schedules/${year}/${month}`,
      });
    } catch (error) {
      if (error instanceof ApiClientError && error.code === 'NOT_FOUND') {
        return null;
      }
      throw error;
    }
  }

  async saveSchedule(schedule: MonthlySchedule): Promise<void> {
    if (USE_MOCK_API) {
      const response = await mockApiClient.saveSchedule(schedule);
      if (!response.success) {
        throw new ApiClientError(
          response.error?.code || 'API_ERROR',
          response.error?.message || 'Failed to save schedule'
        );
      }
      return;
    }

    return this.request<void>({
      method: 'PUT',
      url: '/schedules',
      data: schedule,
    });
  }

  async updateEvent(scheduleId: string, eventId: string, updates: any): Promise<void> {
    return this.request<void>({
      method: 'PUT',
      url: `/schedules/${scheduleId}/events/${eventId}`,
      data: updates,
    });
  }

  async deleteSchedule(year: number, month: number): Promise<void> {
    return this.request<void>({
      method: 'DELETE',
      url: `/schedules/${year}/${month}`,
    });
  }

  // Theme Management API
  async getAvailableThemes(): Promise<any[]> {
    if (USE_MOCK_API) {
      const response = await mockApiClient.getAvailableThemes();
      return response;
    }

    return this.request<any[]>({
      method: 'GET',
      url: '/themes',
    });
  }

  async updateScheduleTheme(scheduleId: string, themeId: string): Promise<MonthlySchedule> {
    if (USE_MOCK_API) {
      const response = await mockApiClient.updateScheduleTheme(scheduleId, themeId);
      return response;
    }

    return this.request<MonthlySchedule>({
      method: 'PUT',
      url: `/schedules/${scheduleId}/theme`,
      data: { themeId },
    });
  }

  async uploadScheduleImage(scheduleId: string, imageFile: File, imageType: 'header' | 'background'): Promise<string> {
    console.log('ApiClient: uploadScheduleImage called', { scheduleId, imageType, fileName: imageFile.name, fileSize: imageFile.size, fileType: imageFile.type });
    
    if (USE_MOCK_API) {
      console.log('ApiClient: Using mock API for schedule image upload');
      const response = await mockApiClient.uploadScheduleImage(scheduleId, imageFile, imageType);
      console.log('ApiClient: Mock API response:', response);
      console.log('ApiClient: Schedule image upload successful, returning URL');
      return response;
    }

    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('type', imageType);

    return this.request<string>({
      method: 'POST',
      url: `/schedules/${scheduleId}/images`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    });
  }

  async uploadMonthlyImage(month: number, imageFile: File, imageType: 'header' | 'background'): Promise<string> {
    console.log('ApiClient: uploadMonthlyImage called', { month, imageType, fileName: imageFile.name, fileSize: imageFile.size, fileType: imageFile.type });
    
    if (USE_MOCK_API) {
      console.log('ApiClient: Using mock API for monthly image upload');
      const response = await mockApiClient.uploadMonthlyImage(month, imageFile, imageType);
      console.log('ApiClient: Mock API response:', response);
      console.log('ApiClient: Monthly image upload successful, returning URL');
      return response;
    }

    const formData = new FormData();
    formData.append('image', imageFile);
    formData.append('type', imageType);

    return this.request<string>({
      method: 'POST',
      url: `/monthly-images/${month}`,
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000,
    });
  }

  async getMonthlyImages(month: number): Promise<{ header?: string; background?: string }> {
    if (USE_MOCK_API) {
      const response = await mockApiClient.getMonthlyImages(month);
      return response;
    }

    return this.request<{ header?: string; background?: string }>({
      method: 'GET',
      url: `/monthly-images/${month}`,
    });
  }

  // やりたいこと（利用者リクエスト）API
  async addUserWish(userName: string, wishText: string): Promise<UserWish> {
    if (USE_MOCK_API) {
      const response = await mockApiClient.addUserWish(userName, wishText);
      if (!response.success) {
        throw new ApiClientError(
          response.error?.code || 'API_ERROR',
          response.error?.message || 'Failed to add wish'
        );
      }
      return response.data!;
    }
    return this.request<UserWish>({
      method: 'POST',
      url: '/user-wishes',
      data: { userName, wishText },
    });
  }

  async getUserWishes(): Promise<UserWish[]> {
    if (USE_MOCK_API) {
      const response = await mockApiClient.getUserWishes();
      if (!response.success) {
        throw new ApiClientError(
          response.error?.code || 'API_ERROR',
          response.error?.message || 'Failed to get wishes'
        );
      }
      return response.data!;
    }
    return this.request<UserWish[]>({
      method: 'GET',
      url: '/user-wishes',
    });
  }

  async deleteUserWish(wishId: string): Promise<void> {
    if (USE_MOCK_API) {
      const response = await mockApiClient.deleteUserWish(wishId);
      if (!response.success) {
        throw new ApiClientError(
          response.error?.code || 'API_ERROR',
          response.error?.message || 'Failed to delete wish'
        );
      }
      return;
    }
    return this.request<void>({
      method: 'DELETE',
      url: `/user-wishes/${wishId}`,
    });
  }

  async login(username: string, password: string): Promise<{ token: string; user: any }> {
    if (USE_MOCK_API) {
      return await mockApiClient.login(username, password);
    }

    return this.request<{ token: string; user: any }>({
      method: 'POST',
      url: '/auth/login',
      data: { username, password },
    });
  }

  async logout(): Promise<void> {
    if (USE_MOCK_API) {
      return await mockApiClient.logout();
    }

    return this.request<void>({
      method: 'POST',
      url: '/auth/logout',
    });
  }

  async verifyToken(): Promise<{ user: any }> {
    if (USE_MOCK_API) {
      return await mockApiClient.verifyToken();
    }

    return this.request<{ user: any }>({
      method: 'GET',
      url: '/auth/verify',
    });
  }
}

// Custom error class for API errors
export class ApiClientError extends Error {
  public code: string;
  public details?: any;
  public timestamp: Date;

  constructor(code: string, message: string, details?: any) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.details = details;
    this.timestamp = new Date();
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient;